import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import {
  filterAndSort, sortArr, money, formatDate, estadoClass,
  clienteName, proyectoTotal, subtotalArticulos, subtotalServicios,
} from "@/lib/helpers";
import { exportExcel, exportPDF, proyectoPDF, proyectoExcel } from "@/lib/exports";
import PaginationBar from "@/components/PaginationBar";
import DatePicker from "@/components/DatePicker";

const blank = () => ({
  numero: "",
  nombre: "",
  clienteId: "",
  fecha: new Date().toISOString().slice(0, 10),
  fechaInicio: "",
  fechaFin: "",
  progreso: 0,
  estado: "Pendiente",
  descripcion: "",
  articulos: [],
  servicios: [],
  personalIds: [],
});

const ESTADOS = ["Pendiente", "En curso", "Finalizado", "Facturado"];

export default function Proyectos({ state, upsert, remove, setView }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "fecha", dir: -1 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blank());
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: -s.dir } : { field, dir: 1 }));

  const allRows = useMemo(
    () => filterAndSort(state.proyectos, search, sort, state),
    [state.proyectos, search, sort, state]
  );
  const pagedRows = allRows.slice(skip, skip + limit);

  const openModal = (row = null) => {
    const b = blank();
    if (row) {
      const data = { ...b, ...row };
      data.articulos = data.articulos || [];
      data.servicios = data.servicios || [];
      data.personalIds = data.personalIds || [];
      // Snapshot titles if missing
      data.articulos.forEach((l) => {
        if (!l.nombre || l.codigo === undefined) {
          const src = state.articulos.find((a) => a.id === l.articuloId);
          if (src) {
            if (!l.nombre) l.nombre = src.nombre || "";
            if (l.codigo === undefined) l.codigo = src.codigo || "";
          }
        }
      });
      data.servicios.forEach((l) => {
        if (!l.nombre || l.codigo === undefined) {
          const src = state.servicios.find((s) => s.id === l.servicioId);
          if (src) {
            if (!l.nombre) l.nombre = src.nombre || "";
            if (l.codigo === undefined) l.codigo = src.codigo || "";
          }
        }
      });
      setForm(data);
    } else {
      b.numero = "P-" + String(state.proyectos.length + 1).padStart(4, "0");
      setForm(b);
    }
    setModal(true);
  };

  const addArticulo = () =>
    setForm((f) => ({ ...f, articulos: [...f.articulos, { articuloId: "", cantidad: 1, precio: 0, nombre: "", codigo: "" }] }));
  const addServicio = () =>
    setForm((f) => ({ ...f, servicios: [...f.servicios, { servicioId: "", cantidad: 1, precio: 0, nombre: "", codigo: "" }] }));

  const updateLine = (list, i, patch) => {
    setForm((f) => ({
      ...f,
      [list]: f[list].map((l, idx) => (idx === i ? { ...l, ...patch } : l)),
    }));
  };
  const removeLine = (list, i) =>
    setForm((f) => ({ ...f, [list]: f[list].filter((_, idx) => idx !== i) }));

  const onSelectArticulo = (i, articuloId) => {
    const a = state.articulos.find((x) => x.id === articuloId);
    const patch = { articuloId };
    if (a) {
      patch.nombre = a.nombre || "";
      patch.codigo = a.codigo || "";
      patch.precio = a.precio || 0;
    }
    updateLine("articulos", i, patch);
  };
  const onSelectServicio = (i, servicioId) => {
    const s = state.servicios.find((x) => x.id === servicioId);
    const patch = { servicioId };
    if (s) {
      patch.nombre = s.nombre || "";
      patch.codigo = s.codigo || "";
      patch.precio = s.precio || 0;
    }
    updateLine("servicios", i, patch);
  };

  const togglePersonal = (pid, checked) => {
    setForm((f) => ({
      ...f,
      personalIds: checked ? [...f.personalIds, pid] : f.personalIds.filter((x) => x !== pid),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    await upsert("proyectos", {
      ...form,
      progreso: Math.max(0, Math.min(100, +form.progreso || 0)),
    });
    toast.success("Proyecto guardado");
    setModal(false);
  };

  const crearVentaDesdeProyecto = (p) => {
    // Delegate via localStorage so Ventas page can pick it up
    const data = {
      clienteId: p.clienteId,
      proyectoId: p.id,
      items: (p.articulos || []).map((l) => ({
        articuloId: l.articuloId, cantidad: +l.cantidad || 0, precio: +l.precio || 0,
        nombre: l.nombre || "", codigo: l.codigo || "",
      })),
      serviciosItems: (p.servicios || []).map((l) => ({
        servicioId: l.servicioId, cantidad: +l.cantidad || 0, precio: +l.precio || 0,
        nombre: l.nombre || "", codigo: l.codigo || "",
      })),
    };
    sessionStorage.setItem("convitel_venta_prefill", JSON.stringify(data));
    toast.success("Venta pre-cargada desde proyecto");
    setView("ventas");
  };

  const progressVal = Math.max(0, Math.min(100, +form.progreso || 0));

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <input className="input max-w-xs" placeholder="Buscar proyecto..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="search-proyectos"/>
        <div className="flex-1"/>
        <button className="btn btn-ghost" onClick={() => exportExcel("proyectos", state) || toast.error("Sin datos")}>Excel</button>
        <button className="btn btn-ghost" onClick={() => exportPDF("proyectos", state) || toast.error("Sin datos")}>PDF</button>
        <button className="btn btn-primary" onClick={() => openModal()} data-testid="btn-new-proyecto">+ Nuevo proyecto</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            {[
              ["numero","Nº"],["nombre","Nombre"],["cliente","Cliente"],
              ["fechaInicio","Inicio"],["fechaFin","Fin"],["estado","Estado"],
              ["progreso","Progreso"],["nArt","Artíc."],["nServ","Serv."],
              ["nPers","Personal"],["total","Total"],
            ].map(([k, l]) => (
              <th key={k} className={`sortable ${["nArt","nServ","nPers","total"].includes(k) ? "text-right " : ""}${sort.field === k ? "active" : ""}`}
                  onClick={() => toggleSort(k)}>
                {l} <span className="arr">{sortArr(sort, k)}</span>
              </th>
            ))}
            <th/>
          </tr></thead>
          <tbody>
            {pagedRows.map((p) => (
              <tr key={p.id}>
                <td className="font-semibold text-[var(--cvt-blue)]">{p.numero || "-"}</td>
                <td className="font-semibold">{p.nombre}</td>
                <td>{clienteName(state, p.clienteId)}</td>
                <td>{formatDate(p.fechaInicio)}</td>
                <td>{formatDate(p.fechaFin)}</td>
                <td><span className={`badge ${estadoClass(p.estado)}`}>{p.estado}</span></td>
                <td>
                  <div className="flex items-center gap-2 min-w-[120px]">
                    <div className="progress-track flex-1">
                      <div className="progress-bar"
                           style={{ width: `${Math.max(0, Math.min(100, +p.progreso || 0))}%` }}/>
                    </div>
                    <span className="text-xs font-semibold text-slate-600 w-9 text-right">
                      {Math.max(0, Math.min(100, +p.progreso || 0))}%
                    </span>
                  </div>
                </td>
                <td className="text-right">{(p.articulos || []).length}</td>
                <td className="text-right">{(p.servicios || []).length}</td>
                <td className="text-right">{(p.personalIds || []).length}</td>
                <td className="text-right font-bold text-[var(--cvt-orange)]">{money(proyectoTotal(p))}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn btn-ghost !py-1 !px-2" onClick={() => openModal(p)}>Editar</button>
                  <button className="btn btn-ghost !py-1 !px-2" title="Descargar PDF del proyecto"
                          onClick={() => proyectoPDF(p, state)} data-testid={`proyecto-pdf-${p.id}`}>PDF</button>
                  <button className="btn btn-ghost !py-1 !px-2" title="Descargar Excel del proyecto"
                          onClick={() => proyectoExcel(p, state)} data-testid={`proyecto-xlsx-${p.id}`}>Excel</button>
                  <button className="btn btn-blue !py-1 !px-2" onClick={() => crearVentaDesdeProyecto(p)}>→ Venta</button>
                  <button className="btn btn-danger !py-1 !px-2" onClick={() => remove("proyectos", p.id)}>✕</button>
                </td>
              </tr>
            ))}
            {allRows.length === 0 && (
              <tr><td colSpan="12" className="text-center text-slate-400 py-6">No hay proyectos</td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar total={allRows.length} skip={skip} limit={limit}
                       onSkipChange={setSkip} onLimitChange={setLimit}/>
      </div>

      {modal && (
        <div className="modal-backdrop">
          <div className="modal p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nuevo"} proyecto</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="space-y-5">
              <div className="grid md:grid-cols-4 gap-4">
                <div><label className="label">Nº Proyecto</label>
                  <input className="input" value={form.numero || ""} onChange={(e) => setForm({ ...form, numero: e.target.value })} placeholder="P-0001" data-testid="proyecto-numero"/></div>
                <div className="md:col-span-2"><label className="label">Nombre *</label>
                  <input className="input" required value={form.nombre || ""} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="proyecto-nombre"/></div>
                <div><label className="label">Cliente *</label>
                  <select className="select" required value={form.clienteId || ""} onChange={(e) => setForm({ ...form, clienteId: e.target.value })} data-testid="proyecto-cliente">
                    <option value="">Selecciona...</option>
                    {state.clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div><label className="label">Estado</label>
                  <select className="select" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })}>
                    {ESTADOS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div><label className="label">Fecha creación</label>
                  <DatePicker value={form.fecha || ""} onChange={(v) => setForm({ ...form, fecha: v })} testid="proyecto-fecha"/></div>
                <div><label className="label">Fecha inicio obra</label>
                  <DatePicker value={form.fechaInicio || ""} onChange={(v) => setForm({ ...form, fechaInicio: v })} testid="proyecto-fecha-inicio"/></div>
                <div><label className="label">Fecha fin obra</label>
                  <DatePicker value={form.fechaFin || ""} onChange={(v) => setForm({ ...form, fechaFin: v })} testid="proyecto-fecha-fin"/></div>
                <div>
                  <label className="label">Progreso <span className="font-bold text-[var(--cvt-orange)]">{progressVal}%</span></label>
                  <div className="flex items-center gap-2">
                    <input type="range" min="0" max="100" step="5" className="flex-1"
                           value={progressVal} onChange={(e) => setForm({ ...form, progreso: +e.target.value })}
                           data-testid="proyecto-progreso-range"/>
                    <input type="number" min="0" max="100" className="input !w-16 text-center"
                           value={progressVal} onChange={(e) => setForm({ ...form, progreso: +e.target.value })}
                           data-testid="proyecto-progreso-input"/>
                  </div>
                  <div className="progress-track mt-2"><div className="progress-bar" style={{ width: `${progressVal}%` }}/></div>
                </div>
                <div className="md:col-span-4"><label className="label">Descripción</label>
                  <input className="input" value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}/></div>
              </div>

              {/* ARTICULOS */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div><div className="font-bold">Artículos vinculados</div><div className="text-xs text-slate-500">Cantidad × precio unitario</div></div>
                  <button type="button" className="btn btn-blue !py-1.5" onClick={addArticulo} data-testid="proyecto-add-articulo">+ Añadir artículo</button>
                </div>
                <table className="data">
                  <thead><tr>
                    <th>Artículo</th><th className="w-28 text-right">Cantidad</th>
                    <th className="w-32 text-right">Precio unit.</th>
                    <th className="w-32 text-right">Subtotal</th><th className="w-10"/>
                  </tr></thead>
                  <tbody>
                    {form.articulos.map((line, i) => (
                      <tr key={i}>
                        <td>
                          <select className="select" value={line.articuloId || ""} onChange={(e) => onSelectArticulo(i, e.target.value)}>
                            <option value="">Selecciona artículo...</option>
                            {state.articulos.map((a) => (
                              <option key={a.id} value={a.id}>{(a.codigo || "") + " · " + a.nombre}</option>
                            ))}
                            {line.articuloId && !state.articulos.find((a) => a.id === line.articuloId) && (line.nombre || line.codigo) && (
                              <option value={line.articuloId}>{(line.codigo || "") + " · " + (line.nombre || "(sin nombre)") + " · (fuera de catálogo)"}</option>
                            )}
                          </select>
                          {line.nombre && (
                            <div className="text-[11px] text-slate-500 mt-1">
                              {line.codigo ? line.codigo + " · " : ""}{line.nombre || ""}
                            </div>
                          )}
                        </td>
                        <td><input type="number" step="1" min="0" className="input text-right"
                                   value={line.cantidad} onChange={(e) => updateLine("articulos", i, { cantidad: +e.target.value })}/></td>
                        <td><input type="number" step="0.01" className="input text-right"
                                   value={line.precio} onChange={(e) => updateLine("articulos", i, { precio: +e.target.value })}/></td>
                        <td className="text-right font-semibold">{money((+line.cantidad || 0) * (+line.precio || 0))}</td>
                        <td><button type="button" className="text-red-500 hover:text-red-700" onClick={() => removeLine("articulos", i)}>✕</button></td>
                      </tr>
                    ))}
                    {form.articulos.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-slate-400 py-2 text-sm">Sin artículos vinculados</td></tr>
                    )}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan="3" className="text-right font-semibold pt-2">Subtotal artículos</td>
                    <td className="text-right font-bold pt-2">{money(subtotalArticulos(form))}</td>
                    <td/>
                  </tr></tfoot>
                </table>
              </div>

              {/* SERVICIOS */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div><div className="font-bold">Servicios</div><div className="text-xs text-slate-500">Tarifa / horas a aplicar</div></div>
                  <button type="button" className="btn btn-blue !py-1.5" onClick={addServicio} data-testid="proyecto-add-servicio">+ Añadir servicio</button>
                </div>
                <table className="data">
                  <thead><tr>
                    <th>Servicio</th><th className="w-28 text-right">Cantidad</th>
                    <th className="w-32 text-right">Precio</th>
                    <th className="w-32 text-right">Subtotal</th><th className="w-10"/>
                  </tr></thead>
                  <tbody>
                    {form.servicios.map((line, i) => (
                      <tr key={i}>
                        <td>
                          <select className="select" value={line.servicioId || ""} onChange={(e) => onSelectServicio(i, e.target.value)}>
                            <option value="">Selecciona servicio...</option>
                            {state.servicios.map((s) => (
                              <option key={s.id} value={s.id}>{(s.codigo || "") + " · " + s.nombre}</option>
                            ))}
                            {line.servicioId && !state.servicios.find((s) => s.id === line.servicioId) && (line.nombre || line.codigo) && (
                              <option value={line.servicioId}>{(line.codigo || "") + " · " + (line.nombre || "(sin nombre)") + " · (fuera de catálogo)"}</option>
                            )}
                          </select>
                          {line.nombre && (
                            <div className="text-[11px] text-slate-500 mt-1">
                              {line.codigo ? line.codigo + " · " : ""}{line.nombre || ""}
                            </div>
                          )}
                        </td>
                        <td><input type="number" step="0.5" min="0" className="input text-right"
                                   value={line.cantidad} onChange={(e) => updateLine("servicios", i, { cantidad: +e.target.value })}/></td>
                        <td><input type="number" step="0.01" className="input text-right"
                                   value={line.precio} onChange={(e) => updateLine("servicios", i, { precio: +e.target.value })}/></td>
                        <td className="text-right font-semibold">{money((+line.cantidad || 0) * (+line.precio || 0))}</td>
                        <td><button type="button" className="text-red-500 hover:text-red-700" onClick={() => removeLine("servicios", i)}>✕</button></td>
                      </tr>
                    ))}
                    {form.servicios.length === 0 && (
                      <tr><td colSpan="5" className="text-center text-slate-400 py-2 text-sm">Sin servicios</td></tr>
                    )}
                  </tbody>
                  <tfoot><tr>
                    <td colSpan="3" className="text-right font-semibold pt-2">Subtotal servicios</td>
                    <td className="text-right font-bold pt-2">{money(subtotalServicios(form))}</td>
                    <td/>
                  </tr></tfoot>
                </table>
              </div>

              {/* PERSONAL */}
              <div className="card p-4">
                <div className="font-bold mb-3">Personal asignado</div>
                <div className="flex flex-wrap gap-2">
                  {state.personal.map((p) => {
                    const checked = form.personalIds.includes(p.id);
                    return (
                      <label key={p.id}
                             className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg cursor-pointer ${checked ? "bg-orange-50 border-orange-300" : "border-slate-200"}`}>
                        <input type="checkbox" checked={checked} onChange={(e) => togglePersonal(p.id, e.target.checked)}/>
                        <span className="text-sm font-medium">{p.nombre}</span>
                      </label>
                    );
                  })}
                  {state.personal.length === 0 && <span className="text-sm text-slate-400">Sin personal registrado todavía</span>}
                </div>
              </div>

              <div className="card p-4 bg-gradient-to-r from-orange-50 to-blue-50">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-slate-700">TOTAL PROYECTO</div>
                  <div className="text-2xl font-extrabold text-[var(--cvt-orange)]">{money(proyectoTotal(form))}</div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="proyecto-save">Guardar proyecto</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
